import { Box, Flex, Text, useToast } from "@chakra-ui/react";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React from "react";
import Select from "../../shared/components/Select";
import useApi from "../../shared/hooks/useApi";
import { OfficeId, SandataPlanOfCareItemId } from "../../shared/schema/schema";
import { formatErrorResponse } from "../../shared/utils/format-response-error";
import ErrorState from "./components/ErrorState";
import LoadingState from "./components/LoadingState";
import MappingTableShell from "./components/MappingTableShell";
import SandataMappingRow from "./components/SandataMappingRow";
import { bySectionThenCode } from "./evv-duty-code-mapping.utils";
import {
  DraftMappingMap,
  OfficeOption,
  SandataItemOption,
  SandataPocMapping,
} from "./sandata-poc-mapping.types";

export default function SandataPocMappingTable() {
  const { api, queries } = useApi();
  const queryClient = useQueryClient();
  const toast = useToast();

  const officesQuery = useQuery(queries.office.list());

  const [selectedOfficeId, setSelectedOfficeId] = React.useState<OfficeId | null>(null);
  // The server narrows the Sandata duty codes to the selected office's state. The previous
  // result is kept while the next office loads so the table does not flash its loading state.
  const mappingsQuery = useQuery({
    ...queries.evvDutyCodeMapping.sandataMappings(selectedOfficeId),
    placeholderData: keepPreviousData,
  });
  const [draftMappings, setDraftMappings] = React.useState<DraftMappingMap>({});
  const [savingPlanOfCareItemId, setSavingPlanOfCareItemId] = React.useState<
    SandataPocMapping["planOfCareItemId"] | null
  >(null);

  const mappings = React.useMemo(
    () => mappingsQuery.data?.mappings ?? [],
    [mappingsQuery.data?.mappings]
  );

  // Only offices that actually have plan of care items can be mapped, so the filter is built
  // from the mappings rather than from the full office list.
  const officeOptions: OfficeOption[] = React.useMemo(() => {
    const officeNameMap = new Map(
      (officesQuery.data?.offices ?? []).map((office) => [office.id, office.name])
    );
    const officeIds = Array.from(new Set(mappings.map((mapping) => mapping.officeId)));

    return officeIds
      .map((officeId) => ({
        value: officeId,
        label: officeNameMap.get(officeId) ?? String(officeId),
      }))
      .sort((left, right) => left.label.localeCompare(right.label));
  }, [officesQuery.data?.offices, mappings]);

  React.useEffect(() => {
    if (selectedOfficeId === null && officeOptions.length > 0) {
      setSelectedOfficeId(officeOptions[0].value);
    }
  }, [officeOptions, selectedOfficeId]);

  const sandataItemOptions: SandataItemOption[] = React.useMemo(
    () =>
      (mappingsQuery.data?.sandataItems ?? []).map((item) => ({
        value: item.id,
        label: `${item.code} - ${item.description}`,
      })),
    [mappingsQuery.data?.sandataItems]
  );

  const visibleMappings = React.useMemo(
    () =>
      mappings.filter((mapping) => mapping.officeId === selectedOfficeId).sort(bySectionThenCode),
    [mappings, selectedOfficeId]
  );

  const saveMutation = useMutation({
    mutationFn: (payload: {
      planOfCareItemId: SandataPocMapping["planOfCareItemId"];
      sandataPlanOfCareItemIds: SandataPlanOfCareItemId[];
    }) =>
      api.post("./sandata_poc_item_mappings", {
        body: {
          planOfCareItemId: payload.planOfCareItemId,
          sandataPlanOfCareItemIds: payload.sandataPlanOfCareItemIds,
        },
      }),
    onSuccess: (_data, variables) => {
      // Drop the draft so the row falls back to the refetched server value.
      setDraftMappings((current) => {
        const next = { ...current };
        delete next[String(variables.planOfCareItemId)];
        return next;
      });

      queryClient.invalidateQueries({ queryKey: ["evv-duty-code-mapping"] });

      toast({ status: "success", title: "Sandata duty codes saved" });
    },
    onError: (error) => {
      toast({ status: "error", title: formatErrorResponse(error) });
    },
    onSettled: () => setSavingPlanOfCareItemId(null),
  });

  if (officesQuery.isLoading || mappingsQuery.isLoading) {
    return <LoadingState label="Loading Sandata duty code mapping..." />;
  }

  if (mappingsQuery.isError) {
    return <ErrorState message={formatErrorResponse(mappingsQuery.error)} />;
  }

  if (officesQuery.isError) {
    return <ErrorState message={formatErrorResponse(officesQuery.error)} />;
  }

  return (
    <Box>
      <Text color="gray.600" mb={4}>
        Plan of Care items only report to Sandata once they are mapped to a Sandata duty code.
        Unmapped items are excluded from the duties submitted with a visit. An item may map to
        several duty codes, in which case the visit submits one Sandata task per code.
      </Text>

      <Flex alignItems="center" gap={3} mb={4}>
        <Text mb={0}>Office</Text>
        <Select
          searchable
          isDisabled={officeOptions.length === 0}
          label="Office"
          multiple={false}
          options={officeOptions}
          size="sm"
          value={selectedOfficeId}
          width="260px"
          onChange={(value) => setSelectedOfficeId(value ?? null)}
        />
      </Flex>

      <MappingTableShell
        emptyMessage="No plan of care items for this office."
        externalColumnHeader="Sandata Duty Codes"
        isEmpty={visibleMappings.length === 0}
      >
        {visibleMappings.map((mapping) => (
          <SandataMappingRow
            key={String(mapping.planOfCareItemId)}
            draftMappings={draftMappings}
            mapping={mapping}
            sandataItemOptions={sandataItemOptions}
            savingPlanOfCareItemId={savingPlanOfCareItemId}
            onChangeDraftMapping={(planOfCareItemId, value) =>
              setDraftMappings((current) => ({
                ...current,
                [String(planOfCareItemId)]: value ?? [],
              }))
            }
            onSave={(savedMapping, value) => {
              setSavingPlanOfCareItemId(savedMapping.planOfCareItemId);
              saveMutation.mutate({
                planOfCareItemId: savedMapping.planOfCareItemId,
                sandataPlanOfCareItemIds: value,
              });
            }}
          />
        ))}
      </MappingTableShell>
    </Box>
  );
}
